
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { IControl, ControlFacet } from '../../models/controls/ControlFacet';
import { IFaceted, unpack, pack, IFacetMapping } from '../../models/Facet';
import { Omit } from '../../utilities/Omit';
import { connectToContext, provideContext } from '../../utilities/components/Context';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { IComponentDecorator, withDisplayName } from '../../utilities/components/Decorators';
import { mapObject } from '../../utilities/objects';
import { createMemoizer } from '../../utilities/Memoizer';

/**
 * @public
 */
export type IControlFaceted = IFaceted<IControl<any, any>>; // tslint:disable-line:no-any

/**
 * Interface for the control handlers available in the current React context.
 * @internal
 */
export interface IControlHandlerContext {
    controlHandlers?: IControlFaceted;
}

/**
 * Validator for context types used by React.
 * @internal
 */
export const CONTROL_HANDLER_CONTEXT_TYPES: React.ValidationMap<IControlHandlerContext> = {
    controlHandlers: PropTypes.object
};

/**
 * Default injected props for `withControlHandler`.
 * @public
 * @deprecated `withControlHandler` should no longer be used.
 */
export interface IControlHandlerProps<TProps, TOverrides> {
    /**
     * The control handler specified by the given control facet.
     */
    controlHandler: IControl<TProps, TOverrides>;
}

const NO_HANDLERS: IControlFaceted = pack({}, {});

/**
 * Decorator which injects a control handler of the given type
 * into the target component.
 * @param controlTypeFacet - The control type which specifies the injection point and contract.
 * @public
 * @deprecated Components should use `itemCache.demandItemFacet` to access control handlers, even those from UX context.
 */
export function withControlHandler<TProps, TOverrides, TInjectedProps = IControlHandlerProps<TProps, TOverrides>>(
    controlTypeFacet: ControlFacet<TProps, TOverrides>,
    mapControlHandlerToProps?: (controlHandler: IControl<TProps, TOverrides>) => TInjectedProps
): IComponentDecorator<TInjectedProps, {}> {
    return <P extends TInjectedProps>(
        component: React.ComponentType<P>
    ): React.ComponentType<Omit<P, keyof TInjectedProps>> => {
        return connectToContext(CONTROL_HANDLER_CONTEXT_TYPES, (
            context: IControlHandlerContext
        ): TInjectedProps => {
            const {
                controlHandlers = NO_HANDLERS
            } = context;

            const {
                controlHandler = controlTypeFacet.default
            } = unpack({
                controlHandler: controlTypeFacet
            }, controlHandlers);

            return mapControlHandlerToProps ? mapControlHandlerToProps(controlHandler) : {
                controlHandler: controlHandler
            } as {} as TInjectedProps;
        })(component);
    };
}

/**
 * Decorator which injects a set of control handlers of given types
 * into the target component, using a facet mapping.
 * @param mapping - A mapping of prop names to the control facets to use for injection.
 * @deprecated Components should use `itemCache.demandItemFacet` to access control handlers, even those from UX context.
 * @public
 */
export function withControlHandlers<TControlFacets extends object>(
    mapping: IFacetMapping<TControlFacets>
): IComponentDecorator<TControlFacets, {}> {
    return <P extends TControlFacets>(
        component: React.ComponentType<P>
    ): React.ComponentType<Omit<P, keyof TControlFacets>> => {
        return connectToContext(CONTROL_HANDLER_CONTEXT_TYPES, (
            context: IControlHandlerContext
        ): TControlFacets => {
            const {
                controlHandlers = NO_HANDLERS
            } = context;

            const controlFacets = {} as TControlFacets;

            for (const key of (Object.keys(mapping) as (keyof TControlFacets)[])) {
                const facet = mapping[key];

                if (!facet) {
                    throw new Error(`No facet mapped to '${key}'.`);
                }

                const {
                    [facet.id]: controlHandler = ControlFacet.get(facet.id).default
                } = controlHandlers;

                controlFacets[key] = controlHandler as {} as TControlFacets[keyof TControlFacets];
            }

            return controlFacets;
        })(component);
    };
}

const controlHandlersMemoizer = createMemoizer((parentControlHandlers: IControlFaceted) => {
    return createMemoizer((childControlHandlers: IControlFaceted) => {
        return {
            ...parentControlHandlers,
            ...mapObject(childControlHandlers, (handler: IControl<{}, {}>, key: string | number | symbol) => {
                // Compose new handlers with existing handlers.

                const control = ControlFacet.get(key as string);

                const parentHandler = control.unpack(parentControlHandlers);

                if (parentHandler) {
                    return control.compose(handler, parentHandler);
                } else {
                    return handler;
                }
            })
        };
    });
});

/**
 * Component which provides a set of control handlers to use within the wrapped component hierarchy.
 * All renderered components within the tree will respect the control handlers provided,
 * presuming they have a target control type and they are not overridden by a nested provider.
 * @public
 */
export const ControlHandlerProvider: React.ComponentType<{
    controlHandlers: IControlFaceted;
}> = connectToContext(CONTROL_HANDLER_CONTEXT_TYPES, (
    context: IControlHandlerContext,
    props: {
        controlHandlers: IControlFaceted;
    }
) => {
    const {
        controlHandlers = NO_HANDLERS
    } = context;

    const {
        controlHandlers: childControlHandlers
    } = props;

    return {
        controlHandlers: controlHandlersMemoizer(controlHandlers)(childControlHandlers)
    };
})(provideContext(CONTROL_HANDLER_CONTEXT_TYPES, (props: {
    controlHandlers: IControlFaceted;
}) => {
    return {
        controlHandlers: props.controlHandlers
    };
}));

/**
 * Creates a render props component which provides control handlers for the given mapping.
 * @public
 * @deprecated Components should use `itemCache.demandItemFacet` to access control handlers, even those from UX context.
 */
export function createWithControlHandlers<TControlFacets extends object>(mapping: IFacetMapping<TControlFacets>):
    React.ComponentType<IRenderProps<TControlFacets>> & {
        propsType: TControlFacets;
    } {
    const component = withControlHandlers(mapping)(
        withDisplayName('WithControlHandlers.Callback')((props: TControlFacets & IRenderProps<TControlFacets>) => {
            const {
                children,
                ...renderProps
            } = props as IRenderProps<TControlFacets>;

            return renderChildrenWithProps(children, renderProps as TControlFacets);
        }));

    // @ts-ignore
    return withDisplayName('WithControlHandlers')(component);
}
